import { createRoute, Link } from "@tanstack/react-router";
import { withPermission } from "../lib/with-permission";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
	RefreshCw,
	Settings,
	AlertCircle,
	Rocket,
	CheckCircle2,
	Circle,
	Pause,
	Play,
	Trash2,
} from "lucide-react";
import { rootRoute } from "./__root";
import { api } from "../lib/api";
import { useAuth } from "../lib/use-auth";
import { useSyncConfigList, useUpdateSyncConfig } from "../hooks/useSyncConfigs";
import { useSyncLogs, useRetrySyncLog } from "../hooks/useSyncLogs";
import {
	useSyncQueueStatus,
	usePauseSyncQueue,
	useResumeSyncQueue,
	useClearSyncQueue,
} from "../hooks/useSyncQueue";
import { Table } from "../components/ui/Table/Table";
import { Pagination } from "../components/ui/Pagination/Pagination";
import { Badge } from "../components/ui/Badge/Badge";
import { Button } from "../components/ui/Button/Button";
import { Select } from "../components/ui/Select/Select";
import { ConfirmDialog } from "../components/ui/ConfirmDialog/ConfirmDialog";
import { ErrorFallback } from "../components/ui/ErrorFallback/ErrorFallback";
import { formatDateTime } from "../lib/date";
import styles from "./Sync.module.css";

export const route = createRoute({
	getParentRoute: () => rootRoute,
	path: "/sync",
	component: withPermission(SyncPage, "sync", "read"),
});

interface SyncStatus {
	type: "success" | "error";
	message: string;
}

const ENTITY_OPTIONS = [
	"items",
	"warehouses",
	"item_warehouses",
	"business_partners",
	"orders",
	"quotations",
	"sales_persons",
];
const STATUS_OPTIONS = ["pending", "in_progress", "synced", "failed", "manual_review"];

function QueueStatusCard() {
	const { t } = useTranslation();
	const { data: queue, isLoading } = useSyncQueueStatus();
	const pause = usePauseSyncQueue();
	const resume = useResumeSyncQueue();
	const clear = useClearSyncQueue();
	const [clearFailed, setClearFailed] = useState(false);
	const [clearStatus, setClearStatus] = useState<SyncStatus | null>(null);
	const [clearOpen, setClearOpen] = useState(false);

	const handleClear = async () => {
		try {
			const result = await clear.mutateAsync({ removeFailed: clearFailed });
			setClearStatus({
				type: "success",
				message:
					t("queueCleared") +
					` (${result.removedWaiting} ${t("waiting").toLowerCase()}, ${result.removedDelayed} ${t("delayed").toLowerCase()}${result.removedFailed ? `, ${result.removedFailed} ${t("failed").toLowerCase()}` : ""})`,
			});
		} catch (err) {
			setClearStatus({
				type: "error",
				message: err instanceof Error ? err.message : t("error"),
			});
		}
	};

	const counts = queue?.counts;

	return (
		<div className={styles.card}>
			<h2 className={styles.cardTitle}>{t("queueStatus")}</h2>
			{isLoading ? (
				<p className={styles.cardDesc}>{t("loading")}</p>
			) : !counts ? (
				<p className={styles.cardDesc}>{t("noData")}</p>
			) : (
				<>
					<div className={styles.queueGrid}>
						<div className={styles.queueItem}>
							<span className={styles.queueValue}>{counts.waiting}</span>
							<span className={styles.queueLabel}>{t("queueWaiting")}</span>
						</div>
						<div className={styles.queueItem}>
							<span className={styles.queueValue}>{counts.active}</span>
							<span className={styles.queueLabel}>{t("queueActive")}</span>
						</div>
						<div className={styles.queueItem}>
							<span className={styles.queueValue}>{counts.delayed}</span>
							<span className={styles.queueLabel}>{t("queueDelayed")}</span>
						</div>
						<div className={styles.queueItem}>
							<span className={styles.queueValue}>{counts.failed}</span>
							<span className={styles.queueLabel}>{t("queueFailed")}</span>
						</div>
						<div className={styles.queueItem}>
							<span className={styles.queueValue}>{counts.completed}</span>
							<span className={styles.queueLabel}>{t("queueCompleted")}</span>
						</div>
						<div className={styles.queueItem}>
							<span className={styles.queueValue}>{queue?.isPaused ? t("yes") : t("no")}</span>
							<span className={styles.queueLabel}>{t("queuePaused")}</span>
						</div>
					</div>
					<div className={styles.queueActions}>
						{queue?.isPaused ? (
							<button
								type="button"
								className={styles.button}
								disabled={resume.isPending}
								onClick={() => resume.mutate()}
							>
								<Play size={14} style={{ marginRight: 6 }} />
								{t("resumeQueue")}
							</button>
						) : (
							<button
								type="button"
								className={styles.button}
								disabled={pause.isPending}
								onClick={() => pause.mutate()}
							>
								<Pause size={14} style={{ marginRight: 6 }} />
								{t("pauseQueue")}
							</button>
						)}
						<button
							type="button"
							className={styles.button}
							disabled={clear.isPending || (!counts.waiting && !counts.delayed)}
							onClick={() => setClearOpen(true)}
						>
							<Trash2 size={14} style={{ marginRight: 6 }} />
							{t("clearQueue")}
						</button>
						<label
							style={{
								display: "flex",
								alignItems: "center",
								gap: 6,
								fontSize: "0.875rem",
								cursor: "pointer",
							}}
						>
							<input
								type="checkbox"
								checked={clearFailed}
								onChange={(e) => setClearFailed(e.target.checked)}
							/>
							{t("clearQueueFailed")}
						</label>
					</div>
					{clearStatus && (
						<div
							className={`${styles.status} ${clearStatus.type === "success" ? styles.statusSuccess : styles.statusError}`}
						>
							{clearStatus.message}
						</div>
					)}
				</>
			)}
			<ConfirmDialog
				isOpen={clearOpen}
				onClose={() => setClearOpen(false)}
				onConfirm={() => {
					handleClear().then(() => setClearOpen(false));
				}}
				message={t("clearQueueConfirm")}
				isLoading={clear.isPending}
			/>
		</div>
	);
}

function SyncPage() {
	const { t } = useTranslation();
	const { user } = useAuth();
	const [loading, setLoading] = useState<Record<string, boolean>>({});
	const [status, setStatus] = useState<SyncStatus | null>(null);
	const [sapEnabled, setSapEnabled] = useState<boolean | null>(null);
	const [bootstrapStatus, setBootstrapStatus] = useState<{ complete: boolean } | null>(null);
	const [bootstrapLoading, setBootstrapLoading] = useState(false);
	const [resetOpen, setResetOpen] = useState(false);

	const { data: configs, isLoading: configsLoading } = useSyncConfigList();
	const updateConfig = useUpdateSyncConfig();

	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(20);
	const [statusFilter, setStatusFilter] = useState("");
	const [entityFilter, setEntityFilter] = useState("");
	const [fullSyncMode, setFullSyncMode] = useState(false);
	const [sinceDays, setSinceDays] = useState(3);

	const {
		data: logsData,
		isLoading: logsLoading,
		error: logsError,
		refetch: refetchLogs,
	} = useSyncLogs({
		status: statusFilter || undefined,
		entity: entityFilter || undefined,
		limit: pageSize,
		offset: (page - 1) * pageSize,
	});

	const retryMutation = useRetrySyncLog();

	useEffect(() => {
		if (user && user.role !== "admin") {
			window.location.href = "/";
		}
	}, [user]);

	useEffect(() => {
		api
			.get<{ enabled: boolean }>("/api/sync/status")
			.then((data) => setSapEnabled(data.enabled))
			.catch(() => setSapEnabled(false));
	}, []);

	useEffect(() => {
		if (sapEnabled) {
			api
				.get<{ complete: boolean }>("/api/sync/bootstrap/status")
				.then((data) => setBootstrapStatus(data))
				.catch(() => setBootstrapStatus({ complete: false }));
		}
	}, [sapEnabled]);

	const runBootstrap = async () => {
		setBootstrapLoading(true);
		setStatus(null);
		try {
			const result = await api.post<{
				success: boolean;
				message: string;
				jobId: string | undefined;
			}>("/api/sync/bootstrap", {});
			setStatus({
				type: "success",
				message: result.jobId ? t("bootstrapInitiated", { jobId: result.jobId }) : result.message,
			});
		} catch (err) {
			setStatus({
				type: "error",
				message: err instanceof Error ? err.message : t("error"),
			});
		} finally {
			setBootstrapLoading(false);
		}
	};

	const resetBootstrapState = async () => {
		try {
			await api.post("/api/sync/bootstrap/reset", {});
			setBootstrapStatus({ complete: false });
			setStatus({ type: "success", message: t("resetBootstrapSuccess") });
		} catch (err) {
			setStatus({
				type: "error",
				message: err instanceof Error ? err.message : t("error"),
			});
		}
	};

	const triggerSync = async (entity: string, direction: string) => {
		const key = `${direction}-${entity}`;
		setLoading((prev) => ({ ...prev, [key]: true }));
		setStatus(null);
		try {
			const result = await api.post<{ jobId: string }>("/api/sync/trigger", {
				entity,
				direction,
				fullSync: fullSyncMode,
				...(fullSyncMode ? {} : { sinceDays }),
			});
			setStatus({
				type: "success",
				message: t("syncTriggered", { entity: t(entity), jobId: result.jobId }),
			});
		} catch (err) {
			setStatus({
				type: "error",
				message: err instanceof Error ? err.message : t("error"),
			});
		} finally {
			setLoading((prev) => ({ ...prev, [key]: false }));
		}
	};

	const toggleEnabled = (entityType: string, current: boolean) => {
		updateConfig.mutate({ entityType, body: { isEnabled: !current } });
	};

	const handleRetry = async (id: string) => {
		try {
			await retryMutation.mutateAsync(id);
		} catch {
			void 0;
		}
	};

	const getStatusVariant = (statusValue: string) => {
		switch (statusValue) {
			case "synced":
				return "success";
			case "failed":
				return "danger";
			case "in_progress":
				return "primary";
			case "pending":
				return "muted";
			case "manual_review":
				return "warning";
			default:
				return "default";
		}
	};

	return (
		<div className={styles.container}>
			<h1 className={styles.heading}>{t("syncTitle")}</h1>

			{sapEnabled === false && (
				<div className={`${styles.card} ${styles.disabledCard}`}>
					<div className={styles.disabledHeader}>
						<AlertCircle size={20} />
						<h2 className={styles.cardTitle}>{t("syncNotConfigured")}</h2>
					</div>
					<p className={styles.cardDesc}>{t("syncNotConfiguredDesc")}</p>
					<Link to="/admin/settings" className={styles.button}>
						<Settings size={16} style={{ marginRight: 8 }} />
						{t("goToSettings")}
					</Link>
				</div>
			)}

			{sapEnabled === true && (
				<>
					<div className={styles.card}>
						<h2 className={styles.cardTitle}>{t("syncBootstrap")}</h2>
						<p className={styles.cardDesc}>{t("syncBootstrapDesc")}</p>
						<div className={styles.bootstrapRow}>
							{bootstrapStatus && (
								<div className={styles.bootstrapStatus}>
									{bootstrapStatus.complete ? (
										<>
											<CheckCircle2 size={18} className={styles.bootstrapIconComplete} />
											<span className={styles.bootstrapTextComplete}>
												{t("syncBootstrapComplete")}
											</span>
										</>
									) : (
										<>
											<Circle size={18} className={styles.bootstrapIconIncomplete} />
											<span className={styles.bootstrapTextIncomplete}>
												{t("syncBootstrapIncomplete")}
											</span>
										</>
									)}
								</div>
							)}
							<button
								type="button"
								className={styles.button}
								disabled={bootstrapLoading}
								onClick={runBootstrap}
							>
								<Rocket size={16} style={{ marginRight: 8 }} />
								{bootstrapLoading ? t("syncBootstrapRunning") : t("syncBootstrapRun")}
							</button>
							<button
								type="button"
								className={styles.button}
								disabled={bootstrapLoading}
								onClick={() => setResetOpen(true)}
								style={{ marginLeft: 8 }}
							>
								<Trash2 size={16} style={{ marginRight: 8 }} />
								{t("reset")}
							</button>
						</div>
					</div>

					<div className={styles.card}>
						<h2 className={styles.cardTitle}>{t("syncManual")}</h2>
						<p className={styles.cardDesc}>{t("syncManualDesc")}</p>
						<div className={styles.syncModeRow}>
							<label className={styles.syncModeItem}>
								<input
									type="radio"
									name="syncMode"
									checked={!fullSyncMode}
									onChange={() => setFullSyncMode(false)}
								/>
								{t("syncLastDays")}
								<input
									type="number"
									min={1}
									max={365}
									value={sinceDays}
									disabled={fullSyncMode}
									onChange={(e) => setSinceDays(Math.max(1, Number(e.target.value) || 1))}
									className={styles.syncDaysInput}
								/>
								{t("day")}
							</label>
							<label className={styles.syncModeItem}>
								<input
									type="radio"
									name="syncMode"
									checked={fullSyncMode}
									onChange={() => setFullSyncMode(true)}
								/>
								{t("syncFull")}
							</label>
						</div>
						<div className={styles.buttonRow}>
							<button
								type="button"
								className={styles.button}
								disabled={loading["inbound-items"]}
								onClick={() => triggerSync("items", "inbound")}
							>
								<RefreshCw size={16} style={{ marginRight: 8 }} />
								{loading["inbound-items"] ? t("syncing") : t("syncItems")}
							</button>
							<button
								type="button"
								className={styles.button}
								disabled={loading["inbound-warehouses"]}
								onClick={() => triggerSync("warehouses", "inbound")}
							>
								<RefreshCw size={16} style={{ marginRight: 8 }} />
								{loading["inbound-warehouses"] ? t("syncing") : t("syncWarehouses")}
							</button>
							<button
								type="button"
								className={styles.button}
								disabled={loading["inbound-business_partners"]}
								onClick={() => triggerSync("business_partners", "inbound")}
							>
								<RefreshCw size={16} style={{ marginRight: 8 }} />
								{loading["inbound-business_partners"] ? t("syncing") : t("syncBusinessPartners")}
							</button>
							<button
								type="button"
								className={styles.button}
								disabled={loading["inbound-orders"]}
								onClick={() => triggerSync("orders", "inbound")}
							>
								<RefreshCw size={16} style={{ marginRight: 8 }} />
								{loading["inbound-orders"] ? t("syncing") : t("syncOrders")}
							</button>
							<button
								type="button"
								className={styles.button}
								disabled={loading["inbound-quotations"]}
								onClick={() => triggerSync("quotations", "inbound")}
							>
								<RefreshCw size={16} style={{ marginRight: 8 }} />
								{loading["inbound-quotations"] ? t("syncing") : t("syncQuotations")}
							</button>
							<button
								type="button"
								className={styles.button}
								disabled={loading["inbound-item_warehouses"]}
								onClick={() => triggerSync("item_warehouses", "inbound")}
							>
								<RefreshCw size={16} style={{ marginRight: 8 }} />
								{loading["inbound-item_warehouses"] ? t("syncing") : t("syncItemWarehouses")}
							</button>
							<button
								type="button"
								className={styles.button}
								disabled={loading["inbound-sales_persons"]}
								onClick={() => triggerSync("sales_persons", "inbound")}
							>
								<RefreshCw size={16} style={{ marginRight: 8 }} />
								{loading["inbound-sales_persons"] ? t("syncing") : t("syncSalesPersons")}
							</button>
						</div>
						{status && (
							<div
								className={`${styles.status} ${status.type === "success" ? styles.statusSuccess : styles.statusError}`}
							>
								{status.message}
							</div>
						)}
					</div>

					<QueueStatusCard />
					<div className={styles.card}>
						<h2 className={styles.cardTitle}>{t("syncScheduled")}</h2>
						<p className={styles.cardDesc}>{t("syncScheduledDesc")}</p>
					</div>

					<div className={styles.card}>
						<h2 className={styles.cardTitle}>{t("syncConfiguration")}</h2>
						{configsLoading ? (
							<p className={styles.cardDesc}>{t("loading")}</p>
						) : !configs || configs.length === 0 ? (
							<p className={styles.cardDesc}>{t("noData")}</p>
						) : (
							<table className={styles.table}>
								<thead>
									<tr>
										<th>{t("entity")}</th>
										<th>{t("direction")}</th>
										<th>{t("conflictResolution")}</th>
										<th>{t("enabled")}</th>
										<th>{t("schedule")}</th>
									</tr>
								</thead>
								<tbody>
									{configs.map((cfg) => (
										<tr key={cfg.entityType}>
											<td>{t(cfg.entityType)}</td>
											<td>{t(cfg.direction)}</td>
											<td>{t(cfg.conflictResolution)}</td>
											<td>
												<button
													type="button"
													className={`${styles.toggle} ${cfg.isEnabled ? styles.toggleEnabled : styles.toggleDisabled}`}
													disabled={updateConfig.isPending}
													onClick={() => toggleEnabled(cfg.entityType, cfg.isEnabled)}
												>
													{cfg.isEnabled ? t("yes") : t("no")}
												</button>
											</td>
											<td>{cfg.scheduleCron ?? t("notAvailable")}</td>
										</tr>
									))}
								</tbody>
							</table>
						)}
					</div>

					<div className={styles.card}>
						<h2 className={styles.cardTitle}>{t("syncLogs")}</h2>
						<div className={styles.filterRow}>
							<Select
								options={[
									{ value: "", label: t("all") },
									...ENTITY_OPTIONS.map((e) => ({ value: e, label: t(e) })),
								]}
								value={entityFilter}
								onChange={(e) => {
									setEntityFilter(e.target.value);
									setPage(1);
								}}
							/>
							<Select
								options={[
									{ value: "", label: t("all") },
									...STATUS_OPTIONS.map((s) => ({ value: s, label: t(s) })),
								]}
								value={statusFilter}
								onChange={(e) => {
									setStatusFilter(e.target.value);
									setPage(1);
								}}
							/>
						</div>
						{logsError ? (
							<ErrorFallback message={logsError.message} onRetry={refetchLogs} />
						) : (
							<>
								<Table
									columns={[
										{
											key: "createdAt",
											header: t("timestamp"),
											render: (row) => formatDateTime(row.createdAt),
											width: "180px",
										},
										{
											key: "entityType",
											header: t("entity"),
											render: (row) => t(row.entityType),
											width: "140px",
										},
										{
											key: "direction",
											header: t("direction"),
											render: (row) => t(row.direction),
											width: "100px",
										},
										{
											key: "status",
											header: t("status"),
											render: (row) => (
												<Badge variant={getStatusVariant(row.status) as never}>
													{t(row.status)}
												</Badge>
											),
											width: "120px",
										},
										{
											key: "operation",
											header: t("operation"),
											width: "140px",
										},
										{
											key: "durationMs",
											header: t("duration"),
											render: (row) =>
												row.durationMs !== null ? `${row.durationMs} ms` : t("notAvailable"),
											width: "100px",
										},
										{
											key: "errorMessage",
											header: t("error"),
											render: (row) =>
												row.errorMessage ? (
													<span
														title={row.errorMessage}
														style={{
															display: "inline-block",
															maxWidth: 200,
															overflow: "hidden",
															textOverflow: "ellipsis",
															whiteSpace: "nowrap",
														}}
													>
														{row.errorMessage}
													</span>
												) : (
													t("notAvailable")
												),
											width: "200px",
										},
										{
											key: "actions",
											header: t("actions"),
											render: (row) =>
												row.status === "failed" ? (
													<Button
														variant="secondary"
														size="sm"
														isLoading={
															retryMutation.isPending && retryMutation.variables === row.id
														}
														onClick={() => handleRetry(row.id)}
													>
														{t("retry")}
													</Button>
												) : null,
											width: "100px",
										},
									]}
									data={logsData?.items ?? []}
									isLoading={logsLoading}
									emptyMessage={t("noData")}
									keyExtractor={(row) => row.id}
								/>
								{logsData && logsData.total > 0 && (
									<Pagination
										page={page}
										pageSize={pageSize}
										total={logsData.total}
										onPageChange={setPage}
										onPageSizeChange={setPageSize}
									/>
								)}
							</>
						)}
					</div>
				</>
			)}
			<ConfirmDialog
				isOpen={resetOpen}
				onClose={() => setResetOpen(false)}
				onConfirm={() => {
					resetBootstrapState().then(() => setResetOpen(false));
				}}
				message={t("resetBootstrapConfirm")}
				isLoading={bootstrapLoading}
			/>
		</div>
	);
}
